import Experience from "@/components/player/Experience";
import ErrorView from "@/components/player/Error";
import Spinner from "@/components/player/Spinner";
import * as ieUtils from "@/common/utils/ieUtils";

import { experienceDataStatus } from "@/stores/experience/types";
import { eventNames } from "@/modules/Constants";
import { isImmersiveLandscapeSize } from "@/modules/orientationManager";
import type { ExperienceState } from "@/types/state";

import CloseXIcon from "@svg/close_x.svg";

type OnApplicationEvent = (event: Record<string, unknown>) => void;

type MainWindowProps = {
  experienceState: ExperienceState;
  onApplicationEvent: OnApplicationEvent;
};

const region = "Main Window";

export default function MainWindow({
  experienceState,
  onApplicationEvent,
}: MainWindowProps) {
  const experienceData = experienceState.experienceData;
  const applicationData = experienceState.applicationData;

  // Mobile landscape's LOADED branch (the immersive layout) renders its own
  // exit affordance (`.l-topbar-exit`, in Experience's landscape header) —
  // omit this button there to avoid a double exit control. Every other
  // combination — portrait, desktop landscape (the split, which has no
  // topbar), or a landscape ERROR/LOADING/UNKNOWN overlay — still needs this
  // button as its only exit control.
  const suppressExitButton =
    applicationData.isLandscape() &&
    isImmersiveLandscapeSize() &&
    experienceState.status === experienceDataStatus.LOADED;

  const exitButton =
    applicationData.hasDesktopExit() && !suppressExitButton ? (
      <button
        className="exit-app hover button square"
        aria-label="Close"
        onClick={() =>
          onApplicationEvent({
            name: eventNames.EXIT,
            region,
          })
        }
      >
        <CloseXIcon className="centered icon-close-x-d" />
      </button>
    ) : null;

  const classNames = [
    "pixmoto-play",
    "content",
    applicationData.isPortrait() ? "portrait" : "landscape",
  ];

  if (experienceData.type) {
    classNames.push(experienceData.type as string);
  }

  if (ieUtils.isIE11()) {
    classNames.push("ie11");
  }

  classNames.push("tw:h-full tw:w-full tw:overflow-hidden");

  if (experienceState.status === experienceDataStatus.ERROR) {
    return (
      <div className={classNames.join(" ")}>
        {exitButton}
        <ErrorView experienceState={experienceState} />
      </div>
    );
  } else if (
    experienceState.status === experienceDataStatus.LOADING ||
    experienceState.status === experienceDataStatus.UNKNOWN
  ) {
    return (
      <div className={classNames.join(" ")}>
        {exitButton}
        <Spinner experienceState={experienceState} />
      </div>
    );
  } else if (experienceState.status === experienceDataStatus.LOADED) {
    return (
      <div className={classNames.join(" ")}>
        {exitButton}
        <Experience
          experienceState={experienceState}
          onApplicationEvent={onApplicationEvent}
        />
      </div>
    );
  } else {
    return null;
  }
}
